import { IconNames } from '@blueprintjs/icons';

import { Files } from 'components/brandedTemplatesAndFiles/Files';
import { Templates } from 'components/brandedTemplatesAndFiles/Templates';
import { TemplatesOverview } from 'components/brandedTemplatesAndFiles/TemplatesOverview';
import { BrandGuidelinesOverview } from 'components/brandGuidelines/BrandGuidelinesOverview';
import { BrandStory } from 'components/brandGuidelines/BrandStory';
import { Colors } from 'components/brandGuidelines/Colors';
import { DefiningOurBrand } from 'components/brandGuidelines/DefiningOurBrand';
import { Downloads } from 'components/brandGuidelines/Downloads';
import { EmailSignature } from 'components/brandGuidelines/EmailSignature';
import { Illustration } from 'components/brandGuidelines/Illustration';
import { Logos } from 'components/brandGuidelines/Logos';
import { Nomenclature } from 'components/brandGuidelines/Nomenclature';
import { Typography } from 'components/brandGuidelines/Typography';
import { BemMethodology } from 'components/designSystem/BemMethodology';
import { ColorPalette } from 'components/designSystem/colorPalette/ColorPalette';
import { DesignSystemOverview } from 'components/designSystem/DesignSystemOverview';
import { DesignTokens } from 'components/designSystem/designTokens/DesignTokens';
import { GettingStartedEngineer } from 'components/designSystem/GettingStartedEngineer';
import { Buttons } from 'components/designSystem/guidelines/Buttons';
import { DateTime } from 'components/designSystem/guidelines/DateTime';
import { DropdownOptions } from 'components/designSystem/guidelines/DropdownOptions';
import { MessagingGuidelines } from 'components/designSystem/guidelines/MessagingGuidelines';
import { VoiceTone } from 'components/designSystem/guidelines/VoiceTone';
import { WritingBestPractices } from 'components/designSystem/guidelines/WritingBestPractices';
import { Personas } from 'components/designSystem/personas/Personas';
import { StrataComponents } from 'components/designSystem/StrataComponents';
import { StrataDesignPrinciples } from 'components/designSystem/StrataDesignPrinciples';
import { UtilityClasses } from 'components/designSystem/utilityClasses/UtilityClasses';
import { AppRoutes } from 'services/routes';

export const SiteSections = [
    {
        title: 'Strata Design System',
        sectionHomeRoute: AppRoutes.designSystemOverview,
        image: 'https://sseasacdn.blob.core.windows.net/strata-website/design-system.svg',
        description:
            'The Strata Design System includes the resources to create user interfaces consistent with our principles, design language, and best practices.',
        navTabContent: [
            {
                navSection: 'General',
                navItems: [
                    {
                        label: 'Strata Design System Overview',
                        to: AppRoutes.designSystemOverview,
                    },
                ],
            },
            {
                navSection: 'Foundation',
                navItems: [
                    {
                        label: 'Color Palette',
                        to: AppRoutes.colorPalette,
                    },
                    {
                        label: 'Design Tokens',
                        to: AppRoutes.designTokens,
                    },
                    {
                        label: 'Utility Classes',
                        to: AppRoutes.utilityClasses,
                    },
                    {
                        label: 'Components',
                        to: AppRoutes.components,
                        rightIcon: IconNames.SHARE,
                    },
                ],
            },
            {
                navSection: 'Resources',
                navItems: [
                    {
                        label: 'Getting Started',
                        to: AppRoutes.gettingStarted,
                    },
                    {
                        label: 'Strata Design Principles',
                        to: AppRoutes.strataDesignPrinciples,
                    },
                    {
                        label: 'Personas',
                        to: AppRoutes.personas,
                    },
                    {
                        label: 'BEM Methodology',
                        to: AppRoutes.bemMethodology,
                    },
                ],
            },
            {
                navSection: 'Guidelines',
                navItems: [
                    {
                        label: 'Buttons',
                        to: AppRoutes.buttons,
                    },
                    {
                        label: 'Date & Time',
                        to: AppRoutes.dateTime,
                    },
                    {
                        label: 'Dropdown Options',
                        to: AppRoutes.dropdownOptions,
                    },
                    {
                        label: 'Messaging Guidelines',
                        to: AppRoutes.messagingGuidelines,
                    },
                    {
                        label: 'Voice & Tone',
                        to: AppRoutes.voiceTone,
                    },
                    {
                        label: 'Writing Best Practices',
                        to: AppRoutes.writingBestPractices,
                    },
                ],
            },
        ],
        routes: [
            { path: AppRoutes.designSystemOverview, element: <DesignSystemOverview /> },
            { path: AppRoutes.bemMethodology, element: <BemMethodology /> },
            { path: AppRoutes.colorPalette, element: <ColorPalette /> },
            { path: AppRoutes.designTokens, element: <DesignTokens /> },
            { path: AppRoutes.gettingStarted, element: <GettingStartedEngineer /> },
            { path: AppRoutes.strataDesignPrinciples, element: <StrataDesignPrinciples /> },
            { path: AppRoutes.personas, element: <Personas /> },
            { path: AppRoutes.components, element: <StrataComponents /> },
            { path: AppRoutes.utilityClasses, element: <UtilityClasses /> },
            { path: AppRoutes.dateTime, element: <DateTime /> },
            { path: AppRoutes.voiceTone, element: <VoiceTone /> },
            { path: AppRoutes.writingBestPractices, element: <WritingBestPractices /> },
            { path: AppRoutes.messagingGuidelines, element: <MessagingGuidelines /> },
            { path: AppRoutes.dropdownOptions, element: <DropdownOptions /> },
            { path: AppRoutes.buttons, element: <Buttons /> },
        ],
    },
    {
        title: 'Brand Guidelines',
        sectionHomeRoute: AppRoutes.brandGuidelinesOverview,
        image: 'https://sseasacdn.blob.core.windows.net/strata-website/brand-guidelines.svg',
        description:
            'Brand guidelines will assist you in designing and producing compelling communications with consistency, creativity, and flexibility.',
        navTabContent: [
            {
                navItems: [
                    {
                        label: 'Brand Guidelines Overview',
                        to: AppRoutes.brandGuidelinesOverview,
                    },
                    {
                        label: 'Defining Our Brand',
                        to: AppRoutes.definingOurBrand,
                    },
                    {
                        label: 'Brand Story',
                        to: AppRoutes.brandStory,
                    },
                    {
                        label: 'Nomenclature',
                        to: AppRoutes.nomenclature,
                    },
                    {
                        label: 'Logos',
                        to: AppRoutes.logos,
                    },
                    {
                        label: 'Colors',
                        to: AppRoutes.colors,
                    },
                    {
                        label: 'Typography',
                        to: AppRoutes.typography,
                    },
                    {
                        label: 'Illustration',
                        to: AppRoutes.illustration,
                    },
                    {
                        label: 'Downloads',
                        to: AppRoutes.downloads,
                    },
                    {
                        label: 'Email Signature',
                        to: AppRoutes.emailSignature,
                    },
                ],
            },
        ],
        routes: [
            { path: AppRoutes.brandGuidelinesOverview, element: <BrandGuidelinesOverview /> },
            { path: AppRoutes.brandStory, element: <BrandStory /> },
            { path: AppRoutes.definingOurBrand, element: <DefiningOurBrand /> },
            { path: AppRoutes.nomenclature, element: <Nomenclature /> },
            { path: AppRoutes.logos, element: <Logos /> },
            // eslint-disable-next-line jsx-a11y/heading-has-content -- False positive
            { path: AppRoutes.typography, element: <Typography /> },
            { path: AppRoutes.emailSignature, element: <EmailSignature /> },
            { path: AppRoutes.colors, element: <Colors /> },
            { path: AppRoutes.illustration, element: <Illustration /> },
            { path: AppRoutes.downloads, element: <Downloads /> },
        ],
    },
    {
        title: 'Templates & Files',
        sectionHomeRoute: AppRoutes.templatesOverview,
        image: 'https://sseasacdn.blob.core.windows.net/strata-website/templates.svg',
        description:
            'An extension of the brand guidelines that includes a repository of brand approved templates for easily producing consistent communications.',
        navTabContent: [
            {
                navItems: [
                    {
                        label: 'Templates & Files Overview',
                        to: AppRoutes.templatesOverview,
                    },
                    {
                        label: 'Templates',
                        to: AppRoutes.templates,
                    },
                    {
                        label: 'Files',
                        to: AppRoutes.files,
                    },
                ],
            },
        ],
        routes: [
            { path: AppRoutes.templatesOverview, element: <TemplatesOverview /> },
            { path: AppRoutes.templates, element: <Templates /> },
            { path: AppRoutes.files, element: <Files /> },
        ],
    },
];
